import { LanCode } from "src/store/reducers/setting";
import { CurrencyIsoCode } from "../enums";
import validator from "validator";
import { Combination } from "src/models/Product";

const getKind = (type: string, combinations: Combination[]) =>
  combinations?.find((c) => c.type === type)?.kind ?? "";

export const getColor = (combinations: Combination[]) =>
  getKind("color", combinations);

export const getSize = (combinations: Combination[]) =>
  getKind("size", combinations);

export const isEmail = (email = "") =>
  !email ? undefined : validator.isEmail(email);

export const isAlphanumeric = (alphanumeric = "") =>
  validator.isAlphanumeric(alphanumeric, "en-US", {
    ignore: " -#&*()_<>~,`'\"{}[]/",
  });

export const debounce = (callback: Function, timeout: number = 300) => {
  let timer: any = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, timeout);
  };
};

export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getCurrency = (languageCode: string) => {
  switch (languageCode) {
    case LanCode.Korean:
      return CurrencyIsoCode.KRW;
    case LanCode.Japanese:
      return CurrencyIsoCode.JPY;
    default:
      return CurrencyIsoCode.USD;
  }
};

export const convertCurrencyToLanguage = (currency?: CurrencyIsoCode) => {
  switch (currency) {
    case CurrencyIsoCode.KRW:
      return LanCode.Korean;
    case CurrencyIsoCode.JPY:
      return LanCode.Japanese;
    default:
      return LanCode.English;
  }
};

// ⚠️ default 가 "$" 라, 통화가 추가될 때 여기 빠뜨리면 €957 이 $957 로 표기된다(값은 그대로).
// EU/GB Fixed price 도입(#5153)으로 EUR/GBP 추가.
export const getCurrencyMark = (currency: CurrencyIsoCode) => {
  switch (currency) {
    case CurrencyIsoCode.JPY:
      return "¥";
    case CurrencyIsoCode.KRW:
      return "₩";
    case CurrencyIsoCode.EUR:
      return "€";
    case CurrencyIsoCode.GBP:
      return "£";
    default:
      return "$";
  }
};

export const getCurrencyString = (c?: CurrencyIsoCode): string => {
  switch (c) {
    case CurrencyIsoCode.USD:
      return "US $";
    case CurrencyIsoCode.JPY:
      return "JP ¥";
    case CurrencyIsoCode.KRW:
      return "KR ";
    case CurrencyIsoCode.EUR:
      return "EUR €";
    case CurrencyIsoCode.GBP:
      return "GBP £";
    default:
      return "US $";
  }
};

const untree = ({ children = [], ...data }) => [
  { ...data, children },
  ...children.flatMap(untree),
];

export function unescapeHtml(str: string) {
  const doc = new DOMParser().parseFromString(str, "text/html");
  return doc.documentElement.textContent ?? "";
}

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPE[c]);
}

export const untreefy = (items: any[]) => items.flatMap(untree);

export const treefy = (items, id: any = 0, idKey = "ID", link = "parent") =>
  items
    ?.filter((item) => item[link] === id)
    .map((item) => {
      return {
        ...item,
        children: treefy(
          items?.filter((item) => item[link] !== id),
          item[idKey],
          idKey,
          link,
        ),
      };
    });

export const capitalize: (text?: string) => string = (text = "") => {
  if (!text || text.length === 1) {
    return text.toUpperCase();
  }
  return text[0].toUpperCase() + text.slice(1);
};

export const getVideoSrcs = (content: string): string[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const videos = doc.querySelectorAll("video");
  let srcs: string[] = [];
  if (videos.length > 0) {
    videos.forEach((v) => {
      srcs = [...srcs, v.src];
    });
  }
  return srcs;
};

export const getQuery = (href: string, key: string) =>
  new URL(href).searchParams.get(key);

/**
 * 배송비 표시 문자열. `amount === 0`(무료)이면 `freeLabel`, 아니면 금액 포맷.
 *
 * `freeLabel` 은 **번역된 문구**를 넘겨야 한다(필수 인자 — 누락 시 컴파일 에러).
 * 문구 언어는 locale 차원이라 currency 로 분기하면 안 된다:
 * 배경과 규칙은 `docs/i18n.md` §문구 언어는 locale 차원.
 */
export const formatShippingPrice = ({
  amount,
  currency,
  countryCode,
  freeLabel,
}: {
  amount: number;
  // formatMoney 와 동일하게 string — geDisplay?.currency 같은 string 소스에 cast 불필요
  currency?: string;
  countryCode?: string | null;
  freeLabel: string;
}): string => {
  if (amount === 0) {
    return freeLabel;
  }

  return formatMoney({
    amount,
    currency,
    countryCode,
  });
};

/**
 * Global-E 통화 표시 게이트 (현재 env 기반 임시) — 단일 소스.
 * true = Global-E 가 통화 변환/표시를 담당하는 환경 → 우리 USD 접미사를 떼고
 * `{symbol} {price}` 공백 포맷 사용. 현재 비-prod(local/dev)=true, staging/prod=false.
 * `formatMoney`(접미사 억제)와 `GePrice`(spaced 포맷)가 이 한 값을 공유한다.
 * TODO(global-e-launch): 런칭 시 이 정의만 `globalEEnabled` flag 기반으로 전환하면
 *   두 동작이 함께 바뀐다(GePrice 는 hook 필요 시 별도). 상세: docs/global-e-storefront.md §4.8.
 */
export const isGlobalEFormatEnv = process.env.NEXT_PUBLIC_ENV !== "production";

// language 파라미터 받는곳이 딱 1군데 여서 currency 만 받로록 변경
export const formatMoney = ({
  amount,
  currency = CurrencyIsoCode.USD,
  countryCode,
  hideCurrency = false,
  spaced = false,
}: {
  amount: number;
  currency?: string;
  hideCurrency?: boolean;
  countryCode?: string | null;
  // Global-E 마커 가격용 `{symbol} {price}` 포맷(예: "$ 200"). docs/global-e-storefront.md §3.3.
  spaced?: boolean;
}): string => {
  const language = convertCurrencyToLanguage(currency as CurrencyIsoCode);
  // 소수가 있는 금액은 2 자리로 채운다. 조건이 종전엔 **`currency === USD`** 로 통화가 박혀 있어
  // EUR/GBP 추가(#5153) 후 **€957.5 가 "€957.5" 로 렌더**됐다(USD 는 "$957.50").
  // 통화 이름 대신 **0 소수점 통화(KRW·JPY)가 아닌가**로 일반화한다.
  //
  // ⚠️ `amount % 1 !== 0` 는 그대로 둔다 — 정수 금액은 소수를 안 붙이는 게 현행 표기이고
  // ($200, €579), 이걸 빼면 US 가격이 전부 "$200.00" 으로 바뀐다(prod 가시 변경).
  //
  // `maximumFractionDigits` 도 `isZeroDecimal` 을 봐야 한다 — 종전엔 KRW 만 0 이라
  // **JPY 비정수가 `¥8,890.5` 로 렌더**됐다(엔화에 소수 단위가 없다). flag 와 무관한
  // 기존 경로다: JP 배송비가 `ForeignExchangeRate.DollarToYen(140) × USD` 라 소수가 난다
  // (`shipping-price-by-sku.ts:119`).
  const isZeroDecimal =
    currency === CurrencyIsoCode.KRW || currency === CurrencyIsoCode.JPY;
  const fractionDigits = {
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
    minimumFractionDigits: !isZeroDecimal && amount % 1 !== 0 ? 2 : 0,
  };

  // Global-E 마커용 포맷: 심볼 뒤 공백(`$ 200`). 마커 가격(GePrice)이 비-prod 에서만
  // 사용 — Global-E 가 통화 변환/표시를 담당하므로 USD 접미사 없이 심볼+공백만.
  if (spaced) {
    const num = new Intl.NumberFormat(language, {
      ...fractionDigits,
      style: "decimal",
    }).format(amount);
    return `${getCurrencyMark(currency as CurrencyIsoCode)} ${num}`;
  }

  const formatted = new Intl.NumberFormat(language, {
    ...fractionDigits,
    style: "currency",
    currency,
  }).format(amount);

  // 비-US + USD 가격에 ` USD` 접미사를 붙여 통화를 명시(타입2 disambiguation,
  // docs/pricing-tiers.md). 단 Global-E 포맷 환경(현재 비-prod)에선 Global-E 스크립트가
  // 비-US 가격을 현지통화로 변환하므로(예: $→AU$), 접미사를 같이 두면 "AU$415 USD"
  // 이중통화가 된다 → 접미사를 떼고 통화 표시를 Global-E 에 맡긴다. 게이트는 단일 소스
  // `isGlobalEFormatEnv` 참조(§4.8).
  const showCurrency =
    !isGlobalEFormatEnv &&
    !hideCurrency &&
    countryCode?.toUpperCase?.() !== "US" &&
    currency === CurrencyIsoCode.USD;
  return showCurrency ? `${formatted} ${currency}` : formatted;
};

export const clamp = (min: number, max: number, value: number): number =>
  Math.min(max, Math.max(min, value));

export const getPercent = (base: number, diff: number): number => {
  return +(100 - ((base - diff) / base) * 100).toFixed(0);
};

export const getURL = (url: string) => {
  try {
    return new URL(url);
  } catch (_) {
    return null;
  }
};
